import type {
  DocumentItem,
  DocumentResponse,
  DocumentStoreResponse,
  IdName,
  LibraryDocumentDownloadStoreRequest,
  LibraryDocumentViewStoreRequest,
  LibraryListParams,
  LibraryListResponse,
} from '@/js/account/entities/document/model/types';
import type { SuccessResponse } from '@/js/account/shared/model';
import { accountHttp } from '@/js/account/shared/api';

export type DocumentStorePayload = {
  title: string
  description?: string | null
  text: string
  readMinutes?: number | null
  tagIds?: number[]
  categoryIds?: number[]
  cover?: File | null
  documentPdf?: File | null
  documentDocx?: File | null
};

export type DocumentUpdatePayload = DocumentStorePayload;

export function buildDocumentStoreFormData(
  payload: DocumentStorePayload,
): FormData {
  const formData = new FormData();
  formData.append('title', payload.title);

  if (payload.description != null && payload.description !== '') {
    formData.append('description', payload.description);
  }

  formData.append('text', payload.text);

  if (payload.readMinutes != null) {
    formData.append('readMinutes', String(payload.readMinutes));
  }

  for (const tagId of payload.tagIds ?? []) {
    formData.append('tags[]', String(tagId));
  }

  for (const categoryId of payload.categoryIds ?? []) {
    formData.append('categories[]', String(categoryId));
  }

  if (payload.cover) {
    formData.append('cover', payload.cover);
  }

  if (payload.documentPdf) {
    formData.append('documentPdf', payload.documentPdf);
  }

  if (payload.documentDocx) {
    formData.append('documentDocx', payload.documentDocx);
  }

  return formData;
}

export function buildDocumentUpdateFormData(
  payload: DocumentUpdatePayload,
): FormData {
  const formData = buildDocumentStoreFormData(payload);
  // PHP only parses multipart bodies for POST; Laravel method spoofing routes to PATCH.
  formData.append('_method', 'PATCH');

  if (!formData.has('readMinutes')) {
    formData.append('readMinutes', '');
  }

  return formData;
}

export async function accountDocumentTagsIndex(): Promise<IdName[]> {
  const { data } = await accountHttp.get<IdName[]>('/document-tags');

  return data;
}

export async function accountDocumentCategoriesIndex(): Promise<IdName[]> {
  const { data } = await accountHttp.get<IdName[]>('/document-categories');

  return data;
}

function buildLibraryListQuery(
  params?: LibraryListParams,
): Record<string, unknown> {
  const query: Record<string, unknown> = {
    search: params?.search,
    sort: params?.sort,
    page: params?.page,
    perPage: params?.perPage,
  };

  if (params?.categories != null && params.categories.length > 0) {
    query['categories[]'] = params.categories;
  }

  if (params?.tags != null && params.tags.length > 0) {
    query['tags[]'] = params.tags;
  }

  return query;
}

export async function accountLibraryIndex(
  params?: LibraryListParams,
): Promise<LibraryListResponse> {
  const { data } = await accountHttp.get<LibraryListResponse>('/library', {
    params: buildLibraryListQuery(params),
  });

  return data;
}

export async function accountDocumentsDownloadsIndex(
  params?: LibraryListParams,
): Promise<LibraryListResponse> {
  const { data } = await accountHttp.get<LibraryListResponse>(
    '/documents/downloads',
    { params: buildLibraryListQuery(params) },
  );

  return data;
}

export async function accountDocumentsShow(id: number): Promise<DocumentResponse> {
  const { data } = await accountHttp.get<DocumentResponse>(
    `/library/documents/${id}`,
  );

  return data;
}

export async function accountDocumentsViewsStore(
  payload: LibraryDocumentViewStoreRequest,
): Promise<SuccessResponse> {
  const { data } = await accountHttp.post<SuccessResponse>(
    '/documents/views',
    payload,
  );

  return data;
}

export type DocumentContentImageResponse = {
  url: string
};

export async function accountDocumentContentImageStore(
  file: File,
): Promise<DocumentContentImageResponse> {
  const formData = new FormData();
  formData.append('image', file);

  const { data } = await accountHttp.post<DocumentContentImageResponse>(
    '/library/documents/images',
    formData,
  );

  return data;
}

export async function accountDocumentsStore(
  payload: DocumentStorePayload,
): Promise<DocumentStoreResponse> {
  const { data } = await accountHttp.post<DocumentStoreResponse>(
    '/library/documents',
    buildDocumentStoreFormData(payload),
  );

  return data;
}

export async function accountDocumentsUpdate(
  id: number,
  payload: DocumentUpdatePayload,
): Promise<SuccessResponse> {
  const { data } = await accountHttp.post<SuccessResponse>(
    `/library/documents/${id}`,
    buildDocumentUpdateFormData(payload),
  );

  return data;
}

export async function accountDocumentsDestroy(id: number): Promise<SuccessResponse> {
  const { data } = await accountHttp.delete<SuccessResponse>(
    `/library/documents/${id}`,
  );

  return data;
}

export async function accountDocumentsMediaDestroy(
  id: number,
  mediaId: number,
): Promise<SuccessResponse> {
  const { data } = await accountHttp.delete<SuccessResponse>(
    `/library/documents/${id}/media/${mediaId}`,
  );

  return data;
}

export async function accountDocumentsRecentlyViewedIndex(params?: {
  limit?: number
}): Promise<DocumentItem[]> {
  const { data } = await accountHttp.get<DocumentItem[]>(
    '/documents/recently-viewed',
    { params },
  );

  return data;
}

export async function accountDocumentsDownloadsStore(
  payload: LibraryDocumentDownloadStoreRequest,
): Promise<SuccessResponse> {
  const { data } = await accountHttp.post<SuccessResponse>(
    '/documents/download-events',
    payload,
  );

  return data;
}
